import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { 
  Scale, 
  Search, 
  Download, 
  RefreshCw, 
  Filter,
  ChevronUp,
  ChevronDown,
  Building2,
  User,
  Briefcase,
  CheckCircle,
  Clock,
  X,
  Mail,
  Phone
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import * as XLSX from 'xlsx';
import './ConsultaInstanciasProbidad.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ConsultaInstanciasProbidad = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [asesoresProbidad, setAsesoresProbidad] = useState([]);
  const [gestiones, setGestiones] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'institucion_nombre', direction: 'asc' });
  
  // Filtros
  const [filtros, setFiltros] = useState({
    institucion: '',
    asesorProbidad: '',
    estado: ''
  });
  
  const [showFilters, setShowFilters] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      // Obtener asesores de probidad del catálogo Y las gestiones para calcular métricas
      const [asesoresRes, gestionesRes] = await Promise.all([
        axios.get(`${API}/asignacion/asesores-probidad`, { headers }),
        axios.get(`${API}/gestiones?limit=5000`, { headers })
      ]);
      
      setAsesoresProbidad(asesoresRes.data || []);
      setGestiones(gestionesRes.data.gestiones || gestionesRes.data || []);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  // Procesar datos combinando catálogo con métricas de gestiones
  const instanciasProbidad = useMemo(() => {
    // Crear mapa de gestiones por asesor de probidad
    const gestionesPorAsesor = {};
    
    gestiones.forEach(g => {
      if (!g.asesor_probidad_nombre && !g.asesor_probidad_id) return;
      
      const key = g.asesor_probidad_id || g.asesor_probidad_nombre;
      if (!gestionesPorAsesor[key]) {
        gestionesPorAsesor[key] = { enProceso: 0, completadas: 0 };
      }
      
      if (g.estado === 'Completada / Archivada') {
        gestionesPorAsesor[key].completadas++;
      } else {
        gestionesPorAsesor[key].enProceso++;
      }
    });
    
    // Combinar datos del catálogo con métricas
    return asesoresProbidad.map(asesor => {
      const metricas = gestionesPorAsesor[asesor.id] || 
                       gestionesPorAsesor[asesor.nombre_completo] || 
                       { enProceso: 0, completadas: 0 };
      
      return {
        id: asesor.id,
        nombre: asesor.nombre_completo,
        email: asesor.email,
        telefono: asesor.telefono || '',
        cargo: asesor.cargo || 'Asesor de Probidad',
        institucion: asesor.institucion_nombre,
        estado: asesor.activo ? 'Activo' : 'Inactivo',
        enProceso: metricas.enProceso,
        completadas: metricas.completadas,
        total: metricas.enProceso + metricas.completadas
      };
    });
  }, [asesoresProbidad, gestiones]);

  // Obtener valores únicos para los filtros
  const valoresUnicos = useMemo(() => {
    return {
      instituciones: [...new Set(instanciasProbidad.map(i => i.institucion).filter(Boolean))].sort(),
      asesores: [...new Set(instanciasProbidad.map(i => i.nombre).filter(Boolean))].sort(),
      estados: ['Activo', 'Inactivo']
    };
  }, [instanciasProbidad]);

  // Filtrar y ordenar datos
  const datosFiltrados = useMemo(() => {
    let resultado = [...instanciasProbidad];
    
    // Aplicar filtros
    if (filtros.institucion) {
      resultado = resultado.filter(i => i.institucion === filtros.institucion);
    }
    if (filtros.asesorProbidad) {
      resultado = resultado.filter(i => i.nombre === filtros.asesorProbidad);
    }
    if (filtros.estado) {
      resultado = resultado.filter(i => i.estado === filtros.estado);
    }
    
    // Ordenar
    if (sortConfig.key) {
      resultado.sort((a, b) => {
        let aVal = a[sortConfig.key] || '';
        let bVal = b[sortConfig.key] || '';
        
        if (typeof aVal === 'number') {
          if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
        
        return sortConfig.direction === 'asc' 
          ? String(aVal).localeCompare(String(bVal))
          : String(bVal).localeCompare(String(aVal));
      });
    }
    
    return resultado;
  }, [instanciasProbidad, filtros, sortConfig]);

  // Manejar ordenamiento
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Renderizar icono de ordenamiento
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
  };

  // Limpiar filtros
  const limpiarFiltros = () => {
    setFiltros({
      institucion: '',
      asesorProbidad: '',
      estado: ''
    });
  };

  // Calcular totales
  const totales = useMemo(() => {
    return {
      asesores: datosFiltrados.length,
      instituciones: [...new Set(datosFiltrados.map(i => i.institucion))].length,
      activos: datosFiltrados.filter(i => i.estado === 'Activo').length,
      enProceso: datosFiltrados.reduce((acc, i) => acc + i.enProceso, 0),
      completadas: datosFiltrados.reduce((acc, i) => acc + i.completadas, 0)
    };
  }, [datosFiltrados]);

  // Exportar a Excel
  const handleExportarExcel = () => {
    const datosExport = datosFiltrados.map(i => ({
      'Institución': i.institucion,
      'Nombre Asesor': i.nombre,
      'Correo Electrónico': i.email,
      'Teléfono': i.telefono || '',
      'Cargo': i.cargo,
      'Estado': i.estado,
      'Gestiones en Proceso': i.enProceso,
      'Gestiones Completadas': i.completadas,
      'Total Gestiones': i.total
    }));
    
    // Agregar fila de totales
    datosExport.push({
      'Institución': 'TOTALES',
      'Nombre Asesor': `${totales.asesores} asesores`,
      'Correo Electrónico': '',
      'Teléfono': '',
      'Cargo': '',
      'Estado': `${totales.activos} activos`,
      'Gestiones en Proceso': totales.enProceso,
      'Gestiones Completadas': totales.completadas,
      'Total Gestiones': totales.enProceso + totales.completadas
    });
    
    const ws = XLSX.utils.json_to_sheet(datosExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Instancias Probidad');
    XLSX.writeFile(wb, `Instancias_Probidad_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  if (loading) {
    return (
      <div className="consulta-loading">
        <div className="loader"></div>
        <p>Cargando instancias de probidad...</p>
      </div>
    );
  }

  return (
    <div className="consulta-probidad-container" data-testid="consulta-instancias-probidad">
      <div className="consulta-header">
        <div>
          <h1>
            <Scale size={28} />
            Instancias de Probidad
          </h1>
          <p>Catálogo de asesores de probidad organizados por institución</p>
        </div>
        <div className="consulta-actions">
          <button className="btn btn-secondary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} />
            Actualizar
          </button>
          <button className="btn btn-primary" onClick={handleExportarExcel}>
            <Download size={16} />
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Tarjetas de Resumen */}
      <div className="resumen-cards">
        <div className="resumen-card">
          <div className="card-icon asesores">
            <User size={24} />
          </div>
          <div className="card-content">
            <span className="card-numero">{totales.asesores}</span>
            <span className="card-label">Asesores de Probidad</span>
          </div>
        </div>
        <div className="resumen-card">
          <div className="card-icon instituciones">
            <Building2 size={24} />
          </div>
          <div className="card-content">
            <span className="card-numero">{totales.instituciones}</span>
            <span className="card-label">Instituciones</span>
          </div>
        </div>
        <div className="resumen-card">
          <div className="card-icon proceso">
            <Clock size={24} />
          </div>
          <div className="card-content">
            <span className="card-numero">{totales.enProceso}</span>
            <span className="card-label">Gestiones en Proceso</span>
          </div>
        </div>
        <div className="resumen-card">
          <div className="card-icon completadas">
            <CheckCircle size={24} />
          </div>
          <div className="card-content">
            <span className="card-numero">{totales.completadas}</span>
            <span className="card-label">Gestiones Completadas</span>
          </div>
        </div>
      </div>

      {/* Panel de Filtros */}
      <div className="filtros-panel">
        <div className="filtros-header" onClick={() => setShowFilters(!showFilters)}>
          <h3>
            <Filter size={18} />
            Filtros de Búsqueda
          </h3>
          <button className="btn-toggle">
            {showFilters ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>
        </div>
        
        {showFilters && (
          <div className="filtros-content">
            <div className="filtros-grid">
              <div className="filtro-item">
                <label><Building2 size={14} /> Institución</label>
                <select
                  value={filtros.institucion}
                  onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}
                >
                  <option value="">Todas las instituciones</option>
                  {valoresUnicos.instituciones.map(inst => (
                    <option key={inst} value={inst}>{inst}</option>
                  ))}
                </select>
              </div>
              
              <div className="filtro-item">
                <label><User size={14} /> Asesor de Probidad</label>
                <select
                  value={filtros.asesorProbidad}
                  onChange={(e) => setFiltros({...filtros, asesorProbidad: e.target.value})}
                >
                  <option value="">Todos los asesores</option>
                  {valoresUnicos.asesores.map(asesor => (
                    <option key={asesor} value={asesor}>{asesor}</option>
                  ))}
                </select>
              </div>
              
              <div className="filtro-item">
                <label><Briefcase size={14} /> Estado</label>
                <select
                  value={filtros.estado}
                  onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
                >
                  <option value="">Todos</option>
                  {valoresUnicos.estados.map(estado => (
                    <option key={estado} value={estado}>{estado}</option>
                  ))}
                </select>
              </div>
            </div>
            
            <div className="filtros-actions">
              <button className="btn btn-secondary" onClick={limpiarFiltros}>
                <X size={16} />
                Limpiar Filtros
              </button>
              <span className="resultados-count">
                {datosFiltrados.length} de {instanciasProbidad.length} registros
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tabla de Resultados */}
      <div className="tabla-container">
        <div className="tabla-wrapper">
          <table className="tabla-probidad" data-testid="tabla-instancias-probidad">
            <thead>
              <tr>
                <th onClick={() => handleSort('institucion')} className="sortable">
                  <Building2 size={14} />
                  Institución {renderSortIcon('institucion')}
                </th>
                <th onClick={() => handleSort('nombre')} className="sortable">
                  <User size={14} />
                  Nombre del Asesor {renderSortIcon('nombre')}
                </th>
                <th onClick={() => handleSort('cargo')} className="sortable">
                  <Briefcase size={14} />
                  Cargo {renderSortIcon('cargo')}
                </th>
                <th onClick={() => handleSort('telefono')} className="sortable">
                  <Phone size={14} />
                  Teléfono {renderSortIcon('telefono')}
                </th>
                <th onClick={() => handleSort('estado')} className="sortable">
                  Estado {renderSortIcon('estado')}
                </th>
                <th onClick={() => handleSort('enProceso')} className="sortable numero-header">
                  <Clock size={14} />
                  En Proceso {renderSortIcon('enProceso')}
                </th>
                <th onClick={() => handleSort('completadas')} className="sortable numero-header">
                  <CheckCircle size={14} />
                  Completadas {renderSortIcon('completadas')}
                </th>
              </tr>
            </thead>
            <tbody>
              {datosFiltrados.length === 0 ? (
                <tr>
                  <td colSpan="7" className="empty-row">
                    <Scale size={48} style={{ opacity: 0.3 }} />
                    <p>No se encontraron registros con los filtros aplicados</p>
                  </td>
                </tr>
              ) : (
                <>
                  {datosFiltrados.map((item, index) => (
                    <tr key={item.id || `${item.nombre}-${index}`}>
                      <td className="institucion-cell" title={item.institucion}>
                        <span className="institucion-badge">
                          <Building2 size={14} />
                          {item.institucion || 'Sin institución'}
                        </span>
                      </td>
                      <td className="asesor-cell">
                        <div className="asesor-info">
                          <div className="asesor-avatar">
                            {item.nombre?.split(' ').map(n => n[0]).slice(0, 2).join('') || '??'}
                          </div>
                          <div className="asesor-details">
                            <span className="asesor-nombre">{item.nombre}</span>
                            {item.email && (
                              <span className="asesor-email">
                                <Mail size={12} />
                                {item.email}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="cargo-cell">{item.cargo}</td>
                      <td className="telefono-cell">
                        {item.telefono ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
                            <Phone size={12} />
                            {item.telefono}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted, #999)' }}>-</span>
                        )}
                      </td>
                      <td className="estado-cell">
                        <span className={`estado-badge ${item.estado.toLowerCase()}`}>
                          {item.estado}
                        </span>
                      </td>
                      <td className="numero-cell proceso">
                        {item.enProceso > 0 ? (
                          <span className="badge-proceso">{item.enProceso}</span>
                        ) : (
                          <span className="badge-cero">0</span>
                        )}
                      </td>
                      <td className="numero-cell completada">
                        {item.completadas > 0 ? (
                          <span className="badge-completada">{item.completadas}</span>
                        ) : (
                          <span className="badge-cero">0</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Fila de totales */}
                  <tr className="fila-totales">
                    <td colSpan="4" className="totales-label">
                      <strong>TOTALES</strong>
                      <span className="totales-info">
                        ({totales.asesores} asesores · {totales.instituciones} instituciones · {totales.activos} activos)
                      </span>
                    </td>
                    <td className="numero-cell proceso">
                      <span className="badge-proceso total">{totales.enProceso}</span>
                    </td>
                    <td className="numero-cell completada">
                      <span className="badge-completada total">{totales.completadas}</span>
                    </td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Información adicional */}
      <div className="info-panel">
        <Scale size={16} />
        <span>
          Esta consulta muestra el catálogo de asesores de instancias de probidad con sus gestiones asignadas.
          Los datos de gestiones se calculan automáticamente a partir de los registros del sistema.
        </span>
      </div>
    </div>
  );
};

export default ConsultaInstanciasProbidad;
